import React, { useState, useMemo } from "react";

const TrainerStoreModal = ({
  isOpen,
  toggle,
  trainerItems,
  trainerInfo,
  loadingTrainerInfo,
  currency,
  buyAmounts,
  handleTrainerQuantityChange,
  handleTrainerPurchase,
  trainerType = "trainer-ev" // "trainer-ev" for EV candies and gummies
}) => {
  const EXTRA_TRAINER_STORE_ITEM_IDS = [60, 61, 161];

  // Derive categories based on trainer type
  const categories = useMemo(() => {
    const cats = ['resources'];
    if (trainerType === "trainer-ev") {
      cats.push('ev-candies', 'gummies');
    }
    return cats;
  }, [trainerType]);

  const [activeCategory, setActiveCategory] = useState('resources');
  const [candySizes, setCandySizes] = useState({}); // Track selected size for each candy stat

  // Helper to build price display arrays - include battle coins
  const currencyKeys = ['money', 'coins', 'ruby', 'sapphire', 'emerald', 'gold', 'diamond'];

  const getImgSrc = (item) => {
    if (item.image) return `/${item.image}`;
    return `/items/${item.id}.png`;
  };

  const hasCost = (item) => {
    return currencyKeys.some(k => (item[k] || 0) > 0) || (item.other || 0) > 0;
  };

  // Helper to extract stat name from candy name (e.g., "HP Candy" -> "HP")
  const getStatFromName = (name) => {
    if (name.includes('HP')) return 'HP';
    if (name.includes('Attack') && !name.includes('Sp.')) return 'Attack';
    if (name.includes('Defense') && !name.includes('Sp.')) return 'Defense';
    if (name.includes('Sp. Attack')) return 'Sp. Attack';
    if (name.includes('Sp. Defense')) return 'Sp. Defense';
    if (name.includes('Speed')) return 'Speed';
    return name.split(' ')[0]; // fallback
  };

  // Helper to extract size from candy name (e.g., "HP Candy L" -> "L")
  const getSizeFromName = (name) => {
    if (name.includes(' XL')) return 'XL';
    if (name.includes(' L')) return 'L';
    return 'Regular';
  };

  // Group candies by stat
  const groupCandiesByStat = (items) => {
    const grouped = {};
    items.forEach(item => {
      const stat = getStatFromName(item.name);
      const size = getSizeFromName(item.name);
      if (!grouped[stat]) {
        grouped[stat] = {};
      }
      grouped[stat][size] = item;
    });
    return grouped;
  };

  // Handle candy size selection
  const handleCandySizeChange = (stat, size) => {
    setCandySizes(prev => ({
      ...prev,
      [stat]: size
    }));
  };

  const getCurrencyIcon = (key) => {
    const icons = {
      money: "/items/money.png",
      coins: "/items/coin.png", 
      battle_coins: "/items/battle_coin.png",
      ruby: "/items/ruby.png",
      sapphire: "/items/sapphire.png",
      emerald: "/items/emerald.png",
      gold: "/items/gold.png",
      diamond: "/items/diamond.png"
    };
    return icons[key] || "/items/coin.png";
  };

  const getCurrencyName = (key) => {
    const names = {
      money: "Money",
      coins: "Coins",
      battle_coins: "Battle Coins",
      ruby: "Ruby",
      sapphire: "Sapphire", 
      emerald: "Emerald",
      gold: "Gold",
      diamond: "Diamond"
    };
    return names[key] || key;
  };

  const getCurrencyColor = (key) => {
    const colors = {
      money: 'rgba(40, 167, 69, 0.2)',
      coins: 'rgba(255, 193, 7, 0.2)',
      battle_coins: 'rgba(74, 144, 226, 0.2)',
      ruby: 'rgba(220, 53, 69, 0.2)',
      sapphire: 'rgba(13, 110, 253, 0.2)',
      emerald: 'rgba(25, 135, 84, 0.2)',
      gold: 'rgba(255, 193, 7, 0.2)',
      diamond: 'rgba(156, 39, 176, 0.2)'
    };
    return colors[key] || 'rgba(108, 117, 125, 0.2)';
  };

  const getCurrencyBorderColor = (key) => {
    const colors = {
      money: 'rgba(40, 167, 69, 0.3)',
      coins: 'rgba(255, 193, 7, 0.3)',
      battle_coins: 'rgba(74, 144, 226, 0.3)',
      ruby: 'rgba(220, 53, 69, 0.3)',
      sapphire: 'rgba(13, 110, 253, 0.3)',
      emerald: 'rgba(25, 135, 84, 0.3)',
      gold: 'rgba(255, 193, 7, 0.3)',
      diamond: 'rgba(156, 39, 176, 0.3)'
    };
    return colors[key] || 'rgba(108, 117, 125, 0.3)';
  };

  if (!isOpen) return null;

  return (
    <div 
      className="game-popup-overlay"
      onClick={toggle}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px 10px',
        boxSizing: 'border-box',
        zIndex: 1050
      }}
    >
      <div 
        className="trainer-store-popup"
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '90%',
          maxWidth: 800,
          maxHeight: '90vh',
          background: 'rgba(29, 34, 46, 0.95)',
          color: '#fff',
          borderRadius: 12,
          backdropFilter: 'blur(10px)',
          border: '2px solid rgba(255, 255, 255, 0.1)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        {/* Header */}
        <div style={{
          padding: '16px 20px',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: 'rgba(0, 0, 0, 0.2)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '20px' }}>🧬</span>
            <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 'bold' }}>EV Training Store</h3>
          </div>
          <button
            onClick={toggle}
            style={{
              background: 'none',
              border: 'none',
              color: '#fff',
              fontSize: '20px',
              cursor: 'pointer',
              padding: '4px 8px',
              borderRadius: '4px',
              opacity: 0.7
            }}
            onMouseEnter={(e) => e.target.style.opacity = '1'}
            onMouseLeave={(e) => e.target.style.opacity = '0.7'}
          >
            ✕
          </button>
        </div>

        {/* Body */}
        <div style={{
          padding: '20px',
          overflowY: 'auto',
          flex: 1
        }}>
          {loadingTrainerInfo ? (
            <div style={{ textAlign: 'center', padding: '40px', fontSize: '16px', opacity: 0.8 }}>
              Loading trainer store...
            </div>
          ) : (
            <div>
              {/* Category tabs */}
              <div style={{ display: 'flex', gap: '8px', marginBottom: '20px', flexWrap: 'wrap' }}>
                {categories.map(cat => (
                  <button
                    key={cat}
                    onClick={() => setActiveCategory(cat)}
                    style={{
                      background: activeCategory === cat ? 'rgba(255, 193, 7, 0.8)' : 'rgba(108, 117, 125, 0.6)',
                      border: activeCategory === cat ? '1px solid rgba(255, 193, 7, 0.8)' : '1px solid rgba(108, 117, 125, 0.4)',
                      color: '#fff',
                      padding: '8px 16px',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '12px',
                      fontWeight: activeCategory === cat ? 'bold' : 'normal',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      if (activeCategory !== cat) {
                        e.target.style.background = 'rgba(108, 117, 125, 0.8)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (activeCategory !== cat) {
                        e.target.style.background = 'rgba(108, 117, 125, 0.6)';
                      }
                    }}
                  >
                    {cat === 'resources' ? 'Resources' : 
                     cat === 'ev-candies' ? 'EV Candies' : 
                     cat === 'gummies' ? 'Gummies' : 
                     cat.charAt(0).toUpperCase() + cat.slice(1)}
                  </button>
                ))}
              </div>

              {/* Resources view */}
              {activeCategory === 'resources' && (
                <div>
                  <h5 style={{ margin: '24px 0 16px', fontSize: '16px', fontWeight: 'bold', color: '#fff' }}>Available Items</h5>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '12px', marginBottom: '20px' }}>
                    {(trainerItems || [])
                      .filter(item => (hasCost(item) || EXTRA_TRAINER_STORE_ITEM_IDS.includes(item.id)) && item.id !== 0)
                      .map(item => {
                        const quantity = buyAmounts[item.id] || 1;
                        const costs = {};
                        
                        // Handle 'other' field (battle coins)
                        const otherCost = (item.other || 0) * quantity;
                        if (otherCost > 0) {
                          costs.other = otherCost;
                        }
                        
                        // Handle other currencies
                        currencyKeys.forEach(k => {
                          costs[k] = (item[k] || 0) * quantity;
                        });
                        
                        // Check affordability including battle coins from 'other' field
                        const canAffordOther = otherCost === 0 || (currency.battle_coins || 0) >= otherCost;
                        const canAffordRegular = currencyKeys.every(k => (currency[k] || 0) >= costs[k]);
                        const canAfford = canAffordOther && canAffordRegular;
                        
                        return (
                          <div key={item.id} style={{
                            background: 'rgba(0, 0, 0, 0.3)',
                            border: '1px solid rgba(255, 255, 255, 0.1)',
                            borderRadius: '8px',
                            padding: '12px',
                            display: 'flex',
                            gap: '12px',
                            alignItems: 'flex-start'
                          }}>
                            <div style={{ flexShrink: 0 }}>
                              <img src={getImgSrc(item)} alt={item.name} style={{width:40,height:40,imageRendering:'pixelated'}} />
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <h6 style={{ margin: '0 0 4px', fontSize: '14px', fontWeight: 'bold', color: '#fff' }}>{item.name}</h6>
                              <p style={{ margin: '0 0 8px', fontSize: '11px', opacity: 0.8, lineHeight: '1.3' }}>{item.desc || item.description || 'No description.'}</p>
                              
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '8px' }}>
                                {/* Show Battle Coins from 'other' field */}
                                {costs.other > 0 && (
                                  <span style={{ 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    gap: '4px', 
                                    fontSize: '11px', 
                                    background: getCurrencyColor('battle_coins'), 
                                    padding: '2px 6px', 
                                    borderRadius: '4px' 
                                  }}>
                                    <img src={getCurrencyIcon('battle_coins')} alt="Battle Coins" style={{ width: 12, height: 12 }} />
                                    {costs.other.toLocaleString()}
                                  </span>
                                )}
                                {/* Show other currencies */}
                                {currencyKeys.map(k => costs[k] > 0 && (
                                  <span key={k} style={{ 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    gap: '4px', 
                                    fontSize: '11px', 
                                    background: getCurrencyColor(k), 
                                    padding: '2px 6px', 
                                    borderRadius: '4px' 
                                  }}>
                                    <img src={getCurrencyIcon(k)} alt={getCurrencyName(k)} style={{ width: 12, height: 12 }} />
                                    {k === 'money' ? `$${costs[k].toLocaleString()}` : costs[k].toLocaleString()}
                                  </span>
                                ))}
                              </div>
                              
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                  <label style={{ fontSize: '11px', opacity: 0.8 }}>Qty:</label>
                                  <input 
                                    type="number" 
                                    min="1" 
                                    max="99" 
                                    value={quantity} 
                                    onChange={(e) => handleTrainerQuantityChange(item.id, e.target.value)}
                                    style={{
                                      width: '50px',
                                      padding: '2px 4px',
                                      background: 'rgba(255, 255, 255, 0.1)',
                                      border: '1px solid rgba(255, 255, 255, 0.2)',
                                      borderRadius: '4px',
                                      color: '#fff',
                                      fontSize: '11px'
                                    }}
                                  />
                                </div>
                                <button 
                                  onClick={() => handleTrainerPurchase(item)} 
                                  disabled={!canAfford}
                                  style={{
                                    background: !canAfford ? 'rgba(108, 117, 125, 0.5)' : 'rgba(74, 144, 226, 0.8)',
                                    border: 'none',
                                    color: '#fff',
                                    padding: '6px 12px',
                                    borderRadius: '4px',
                                    cursor: !canAfford ? 'not-allowed' : 'pointer',
                                    fontSize: '11px',
                                    fontWeight: 'bold',
                                    opacity: !canAfford ? 0.6 : 1
                                  }}
                                >
                                  {!canAfford ? "Can't Afford" : 'Buy'}
                                </button>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                  </div>
                </div>
              )}

              {/* EV Candies view with size selection */}
              {activeCategory === 'ev-candies' && (() => {
                const candyItems = (trainerItems || [])
                  .filter(item => item.name?.toLowerCase().includes('candy') || item.type === 'ev-candy')
                  .filter(item => hasCost(item));
                const groupedCandies = groupCandiesByStat(candyItems);
                
                return (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '12px', marginBottom: '20px' }}>
                    {Object.entries(groupedCandies).map(([stat, sizes]) => {
                      const selectedSize = candySizes[stat] || 'Regular';
                      const selectedItem = sizes[selectedSize] || sizes['Regular'] || Object.values(sizes)[0];
                      if (!selectedItem) return null;
                      
                      const quantity = buyAmounts[selectedItem.id] || 1;
                      const costs = {};
                      
                      // Handle 'other' field (battle coins)
                      const otherCost = (selectedItem.other || 0) * quantity;
                      if (otherCost > 0) {
                        costs.other = otherCost;
                      }
                      
                      // Handle other currencies
                      currencyKeys.forEach(k => {
                        costs[k] = (selectedItem[k] || 0) * quantity;
                      });
                      
                      // Check affordability including battle coins from 'other' field
                      const canAffordOther = otherCost === 0 || (currency.battle_coins || 0) >= otherCost;
                      const canAffordRegular = currencyKeys.every(k => (currency[k] || 0) >= costs[k]);
                      const canAfford = canAffordOther && canAffordRegular;
                      
                      return (
                        <div key={stat} style={{
                          background: 'rgba(0, 0, 0, 0.3)',
                          border: '1px solid rgba(255, 255, 255, 0.1)',
                          borderRadius: '8px',
                          padding: '12px',
                          display: 'flex',
                          gap: '12px',
                          alignItems: 'flex-start'
                        }}>
                          <div style={{ flexShrink: 0 }}>
                            <img src={getImgSrc(selectedItem)} alt={selectedItem.name} style={{width:40,height:40,imageRendering:'pixelated'}} />
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <h6 style={{ margin: '0 0 4px', fontSize: '14px', fontWeight: 'bold', color: '#fff' }}>{stat} Candy</h6>
                            
                            {/* Size Selection Dropdown */}
                            <div style={{ marginBottom: '8px' }}>
                              <label style={{ fontSize: '11px', opacity: 0.8, marginRight: '8px' }}>Size:</label>
                              <select 
                                value={selectedSize}
                                onChange={(e) => handleCandySizeChange(stat, e.target.value)}
                                style={{
                                  background: 'rgba(0, 0, 0, 0.5)',
                                  border: '1px solid rgba(255, 255, 255, 0.2)',
                                  borderRadius: '4px',
                                  color: '#fff',
                                  fontSize: '11px',
                                  padding: '2px 6px'
                                }}
                              >
                                {Object.keys(sizes).map(size => (
                                  <option key={size} value={size}>{size}</option>
                                ))}
                              </select>
                            </div>
                            
                            <p style={{ margin: '0 0 8px', fontSize: '11px', opacity: 0.8, lineHeight: '1.3' }}>{selectedItem.desc || selectedItem.description || 'No description.'}</p>
                            
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '8px' }}>
                              {/* Show Battle Coins from 'other' field */}
                              {costs.other > 0 && (
                                <span style={{ 
                                  display: 'flex', 
                                  alignItems: 'center', 
                                  gap: '4px', 
                                  fontSize: '11px', 
                                  background: getCurrencyColor('battle_coins'), 
                                  padding: '2px 6px', 
                                  borderRadius: '4px' 
                                }}>
                                  <img src={getCurrencyIcon('battle_coins')} alt="Battle Coins" style={{ width: 12, height: 12 }} />
                                  {costs.other.toLocaleString()}
                                </span>
                              )}
                              {/* Show other currencies */}
                              {currencyKeys.map(k => costs[k] > 0 && (
                                <span key={k} style={{ 
                                  display: 'flex', 
                                  alignItems: 'center', 
                                  gap: '4px', 
                                  fontSize: '11px', 
                                  background: getCurrencyColor(k), 
                                  padding: '2px 6px', 
                                  borderRadius: '4px' 
                                }}>
                                  <img src={getCurrencyIcon(k)} alt={getCurrencyName(k)} style={{ width: 12, height: 12 }} />
                                  {k === 'money' ? `$${costs[k].toLocaleString()}` : costs[k].toLocaleString()}
                                </span>
                              ))}
                            </div>
                            
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                <label style={{ fontSize: '11px', opacity: 0.8 }}>Qty:</label>
                                <input 
                                  type="number" 
                                  min="1" 
                                  max="99" 
                                  value={quantity}
                                  onChange={(e) => handleTrainerQuantityChange(selectedItem.id, e.target.value)}
                                  style={{
                                    width: '50px',
                                    padding: '2px 4px',
                                    fontSize: '11px',
                                    background: 'rgba(0, 0, 0, 0.5)',
                                    border: '1px solid rgba(255, 255, 255, 0.2)',
                                    borderRadius: '4px',
                                    color: '#fff',
                                    textAlign: 'center'
                                  }}
                                />
                              </div>
                              <button
                                onClick={() => handleTrainerPurchase(selectedItem)}
                                disabled={!canAfford}
                                style={{
                                  padding: '4px 12px',
                                  fontSize: '11px',
                                  fontWeight: 'bold',
                                  border: 'none',
                                  borderRadius: '4px',
                                  cursor: canAfford ? 'pointer' : 'not-allowed',
                                  background: canAfford ? 'linear-gradient(135deg, #28a745, #20c997)' : 'rgba(108, 117, 125, 0.5)',
                                  color: canAfford ? '#fff' : 'rgba(255, 255, 255, 0.5)',
                                  transition: 'all 0.2s ease'
                                }}
                              >
                                 Buy
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
              
              {/* Gummies view (unchanged) */}
              {activeCategory === 'gummies' && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '12px', marginBottom: '20px' }}>
                  {(trainerItems || [])
                    .filter(item => item.name?.toLowerCase().includes('gummy') || item.type === 'gummy')
                    .filter(item => hasCost(item))
                    .map(item => {
                      const quantity = buyAmounts[item.id] || 1;
                      const costs = {};
                      
                      // Handle 'other' field (battle coins)
                      const otherCost = (item.other || 0) * quantity;
                      if (otherCost > 0) {
                        costs.other = otherCost;
                      }
                      
                      // Handle other currencies
                      currencyKeys.forEach(k => {
                        costs[k] = (item[k] || 0) * quantity;
                      });
                      
                      // Check affordability including battle coins from 'other' field
                      const canAffordOther = otherCost === 0 || (currency.battle_coins || 0) >= otherCost;
                      const canAffordRegular = currencyKeys.every(k => (currency[k] || 0) >= costs[k]);
                      const canAfford = canAffordOther && canAffordRegular;
                      
                      return (
                        <div key={item.id} style={{
                          background: 'rgba(0, 0, 0, 0.3)',
                          border: '1px solid rgba(255, 255, 255, 0.1)',
                          borderRadius: '8px',
                          padding: '12px',
                          display: 'flex',
                          gap: '12px',
                          alignItems: 'flex-start'
                        }}>
                          <div style={{ flexShrink: 0 }}>
                            <img src={getImgSrc(item)} alt={item.name} style={{width:40,height:40,imageRendering:'pixelated'}} />
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <h6 style={{ margin: '0 0 4px', fontSize: '14px', fontWeight: 'bold', color: '#fff' }}>{item.name}</h6>
                            <p style={{ margin: '0 0 8px', fontSize: '11px', opacity: 0.8, lineHeight: '1.3' }}>{item.desc || item.description || 'No description.'}</p>
                            
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '8px' }}>
                              {/* Show Battle Coins from 'other' field */}
                              {costs.other > 0 && (
                                <span style={{ 
                                  display: 'flex', 
                                  alignItems: 'center', 
                                  gap: '4px', 
                                  fontSize: '11px', 
                                  background: getCurrencyColor('battle_coins'), 
                                  padding: '2px 6px', 
                                  borderRadius: '4px' 
                                }}>
                                  <img src={getCurrencyIcon('battle_coins')} alt="Battle Coins" style={{ width: 12, height: 12 }} />
                                  {costs.other.toLocaleString()}
                                </span>
                              )}
                              {/* Show other currencies */}
                              {currencyKeys.map(k => costs[k] > 0 && (
                                <span key={k} style={{ 
                                  display: 'flex', 
                                  alignItems: 'center', 
                                  gap: '4px', 
                                  fontSize: '11px', 
                                  background: getCurrencyColor(k), 
                                  padding: '2px 6px', 
                                  borderRadius: '4px' 
                                }}>
                                  <img src={getCurrencyIcon(k)} alt={getCurrencyName(k)} style={{ width: 12, height: 12 }} />
                                  {k === 'money' ? `$${costs[k].toLocaleString()}` : costs[k].toLocaleString()}
                                </span>
                              ))}
                            </div>
                            
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                <label style={{ fontSize: '11px', opacity: 0.8 }}>Qty:</label>
                                <input 
                                  type="number" 
                                  min="1" 
                                  max="99" 
                                  value={quantity} 
                                  onChange={(e) => handleTrainerQuantityChange(item.id, e.target.value)}
                                  style={{
                                    width: '50px',
                                    padding: '2px 4px',
                                    background: 'rgba(255, 255, 255, 0.1)',
                                    border: '1px solid rgba(255, 255, 255, 0.2)',
                                    borderRadius: '4px',
                                    color: '#fff',
                                    fontSize: '11px'
                                  }}
                                />
                              </div>
                              <button 
                                onClick={() => handleTrainerPurchase(item)} 
                                disabled={!canAfford}
                                style={{
                                  background: !canAfford ? 'rgba(108, 117, 125, 0.5)' : 'rgba(74, 144, 226, 0.8)',
                                  border: 'none',
                                  color: '#fff',
                                  padding: '6px 12px',
                                  borderRadius: '4px',
                                  cursor: !canAfford ? 'not-allowed' : 'pointer',
                                  fontSize: '11px',
                                  fontWeight: 'bold',
                                  opacity: !canAfford ? 0.6 : 1
                                }}
                              >
                                {!canAfford ? "Can't Afford" : 'Buy'}
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TrainerStoreModal;
